import { type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'clsx';
import { Avatar } from 'r/ui';
import type { Profile as ProfileType } from 'r/types';
import LogoIcon from './LogoIcon';

type AppLayoutProps = {
  profile?: ProfileType | null;
  onSignIn?: () => void;
  children: ReactNode;
};

export function AppLayout({ profile, onSignIn, children }: AppLayoutProps) {
  const handleSignIn = () => {
    onSignIn?.();
  };

  return (
    <div className="relative flex flex-col min-h-screen w-full bg-transparent md:bg-background">
      {/* Top Bar */}
      <header className="w-full flex items-center-safe min-h-12 max-w-360 px-4 md:px-10 py-2 md:pt-3 md:pb-4 md:py-5 mx-auto z-10 bg-background md:shadow-[0_4px_8px_0_rgba(110,119,141,0.10)] rounded-b-[20px] md:rounded-b-4xl">
        <div className="w-full min-h-px flex items-center justify-between md:justify-normal gap-6">
          <NavLink to="/" state={{ reset: true }}>
            <LogoIcon className="w-6 h-6" />
          </NavLink>
          <NavLink
            to="https://sanitize.page/leaderboard"
            target="_blank"
            className={({ isActive }) =>
              cn(
                'order-1 md:order-2 hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-bg-main text-main font-medium border border-[var(--textTertiary)]',
                { 'bg-bg-main': isActive },
              )
            }
          >
            <span className="hidden md:inline-block">Go to Sanitizer</span>
          </NavLink>
          {profile ?
            <div className="flex items-center justify-end gap-6 order-3 md:grow">
              {/* Desktop Navigation */}
              <div className="order-2 hidden md:flex w-px h-9 bg-(--textTertiary)" />
              <NavLink
                className="absolute md:relative right-2 md:right-0 order-4 flex items-center gap-2 px-3 rounded-full py-1.5 text-xs md:text-body-16 bg-bg-main text-main font-semibold cursor-pointer"
                to="/profile"
              >
                <span className="hidden md:inline-flex">
                  <Avatar
                    size={32}
                    user={{ avatar: profile.avatar, id: profile.id }}
                  />
                </span>
                <span>
                  {profile.username ??
                    `${profile.address.slice(0, 6)}...${profile.address.slice(-6)}`}
                </span>
              </NavLink>
            </div>
          : <div className="order-4 flex grow justify-end">
              <button
                onClick={handleSignIn}
                className="flex items-center gap-2 px-3 rounded-lg py-2 text-xs md:text-sm bg-(--focus) text-inverse font-medium cursor-pointer"
              >
                Sign In
              </button>
            </div>
          }
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-0 bg-main md:bg-secondary grow pb-20 md:pb-0">
        {children}
      </main>
    </div>
  );
}
